/*
 * WOW Cart - our own slide-out cart, run side by side with Rebuy's Smart Cart.
 *
 * Arm A (Rebuy): this file only stamps the cart with the test group.
 * Arm B (ours):  this file is the cart.
 *
 * The group comes from snippets/wow-cart-head.liquid (window.WowCartConfig).
 * Source of truth: storefront/wow-cart/ in the WOW repo - never edit this in the
 * Shopify theme editor, the next deploy overwrites it.
 */
(function (root) {
  'use strict';

  /* ---------- pure helpers (unit-tested in storefront/wow-cart/test) ---------- */

  function formatMoney(cents, format) {
    var value = (Number(cents) || 0) / 100;
    var fmt = format || '${{amount}}';
    function withCommas(n, decimals) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      if (key === 'amount_no_decimals') return withCommas(Math.round(value), 0);
      return withCommas(value, 2);
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // What counts toward free shipping: everything except excluded add-ons
  // (the shipping-protection line is not merchandise).
  // A free gift put in the cart by storefront/wow-gift (it marks its lines).
  function isGiftLine(item) {
    return !!(item && item.properties && item.properties._wow_gift != null);
  }

  function progressSubtotal(cart, excludeHandles) {
    var skip = excludeHandles || [];
    return (cart.items || []).reduce(function (sum, item) {
      if (isGiftLine(item)) return sum;
      return skip.indexOf(item.handle) === -1 ? sum + item.final_line_price : sum;
    }, 0);
  }

  // The bar with several steps (free shipping, then the free gifts). The steps
  // sit in equal columns, so the fill is worked out step by step: reaching
  // step 2 of 3 fills two thirds, whatever the dollar distance between them.
  function tierProgress(cart, tiers, excludeHandles) {
    var subtotal = progressSubtotal(cart, excludeHandles);
    var list = (tiers || []).slice().sort(function (a, b) { return a.threshold_cents - b.threshold_cents; });
    var next = null, done = 0, floor = 0;
    var steps = list.map(function (t) {
      var reached = subtotal >= t.threshold_cents;
      if (reached) { done++; floor = t.threshold_cents; } else if (!next) next = t;
      return { type: t.type || 'product', label: t.label, title: t.title, threshold_cents: t.threshold_cents, reached: reached };
    });
    var percent = 100;
    if (next && list.length) {
      var span = next.threshold_cents - floor;
      var part = span > 0 ? (subtotal - floor) / span : 0;
      percent = Math.max(0, Math.min(100, Math.round(((done + part) / list.length) * 100)));
    }
    return {
      steps: steps, percent: percent, next: next,
      remaining: next ? next.threshold_cents - subtotal : 0,
      last: steps.length ? steps[steps.length - 1] : null
    };
  }

  function progressState(cart, thresholdCents, excludeHandles) {
    var subtotal = progressSubtotal(cart, excludeHandles);
    var remaining = Math.max(0, thresholdCents - subtotal);
    var percent = thresholdCents > 0 ? Math.min(100, Math.round((subtotal / thresholdCents) * 100)) : 100;
    return { reached: remaining === 0, remaining: remaining, percent: percent };
  }

  function pickUpsells(candidates, cart, opts) {
    var inCart = {};
    (cart.items || []).forEach(function (i) { inCart[i.product_id] = true; });
    var seen = {}, out = [];
    (candidates || []).forEach(function (p) {
      if (!p || out.length >= opts.limit) return;
      if (seen[p.id] || inCart[p.id]) return;
      if (!p.available || p.gift_card) return;
      if ((opts.excludeHandles || []).indexOf(p.handle) !== -1) return;
      if (!(p.variants || []).some(function (v) { return v.available && v.price > 0; })) return;
      seen[p.id] = true;
      out.push(p);
    });
    return out;
  }

  // Order of preference for "you may also like": what our own customers buy
  // with the items in the cart (newest item first), then Shopify's suggestions,
  // then the bestsellers. Capped so the cart never loads a long tail of products.
  function candidateHandles(cart, recs, shopifyHandles, fallback, max) {
    var inCart = {}, out = [];
    var items = (cart.items || []).slice().reverse();
    items.forEach(function (i) { inCart[i.handle] = true; });
    function take(list) {
      (list || []).forEach(function (h) {
        if (h && !inCart[h] && out.indexOf(h) === -1 && out.length < max) out.push(h);
      });
    }
    var pairs = (recs && recs.pairs) || {};
    var lists = items.map(function (i) { return pairs[i.handle] || []; });
    var longest = lists.reduce(function (n, l) { return Math.max(n, l.length); }, 0);
    for (var rank = 0; rank < longest; rank++) {
      take(lists.map(function (l) { return l[rank]; }));
    }
    take(shopifyHandles);
    take(recs && recs.bestsellers);
    take(fallback);
    return out;
  }

  function intervalLabel(interval) {
    return 'Delivers every ' + interval;
  }

  function planPercent(plan) {
    return plan && plan.adjustment_type === 'percentage' ? Number(plan.adjustment_value) || 0 : 0;
  }

  function upgradeLabel(plan) {
    var pct = planPercent(plan);
    return pct ? 'Upgrade to Subscription & Save ' + pct + '%' : 'Upgrade to Subscription';
  }

  function isCartMutation(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url || ''));
  }

  function isCartAdd(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  }

  var helpers = {
    formatMoney: formatMoney, escapeHtml: escapeHtml, progressState: progressState,
    pickUpsells: pickUpsells, candidateHandles: candidateHandles, intervalLabel: intervalLabel, upgradeLabel: upgradeLabel,
    isCartMutation: isCartMutation, isCartAdd: isCartAdd,
    isGiftLine: isGiftLine, tierProgress: tierProgress
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = helpers; return; }

  /* ---------- browser only from here ---------- */

  var CFG = root.WowCartConfig;
  if (!CFG) return;
  var doc = root.document;
  var ownCalls = 0;          // >0 while this file is the one talking to /cart
  var cart = null;
  var productCache = {};
  var busy = false;

  function money(c) { return formatMoney(c, CFG.moneyFormat); }

  // XMLHttpRequest on purpose, not fetch. The shipping-protection app (Mule)
  // re-reads the cart when a cart request FINISHES only for XHR; for fetch it
  // reacts when the request STARTS and prices its checkout button off the old
  // cart. Rebuy talks XHR too, which is why its cart never showed the bug.
  function api(path, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new root.XMLHttpRequest();
      ownCalls++;
      try { xhr.open(body ? 'POST' : 'GET', path); } finally { ownCalls--; }
      xhr.setRequestHeader('Accept', 'application/json');
      if (body) xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function () {
        var json = null;
        try { json = JSON.parse(xhr.responseText); } catch (e) { /* handled below */ }
        if (xhr.status >= 200 && xhr.status < 300 && json) { resolve(json); return; }
        var err = new Error((json && (json.description || json.message)) || 'Cart error');
        err.data = json;
        reject(err);
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(body ? JSON.stringify(body) : null);
    });
  }

  function getProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    var key = 'wow_cart_p_' + handle;
    try {
      var hit = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (hit && Date.now() - hit.t < 15 * 60 * 1000) return (productCache[handle] = Promise.resolve(hit.p));
    } catch (e) { /* ignore */ }
    productCache[handle] = root.fetch(CFG.routes.root.replace(/\/$/, '') + '/products/' + handle + '?section_id=wow-cart-product')
      .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.text(); })
      .then(function (html) {
        var m = html.match(/<script[^>]*data-wow-cart-product[^>]*>([\s\S]*?)<\/script>/);
        var p = JSON.parse(m[1]);
        try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), p: p })); } catch (e) { /* ignore */ }
        return p;
      })
      .catch(function () { delete productCache[handle]; return null; });
    return productCache[handle];
  }

  /* ---------- group tag on the cart (both arms) ---------- */

  // `force` rewrites the tag even when it is already there: the write is a
  // finished cart request, which is what makes Mule re-price its button after
  // the theme or another app changed the cart through fetch.
  function tagCart(c, force) {
    if (!CFG.tag || !c || !c.item_count) return Promise.resolve(c);
    var attrs = c.attributes || {};
    var tagged = attrs._wow_cart === CFG.tag && attrs._wow_cart_exp === CFG.experiment;
    if (tagged && !force) return Promise.resolve(c);
    return api('/cart/update.js', { attributes: { _wow_cart: CFG.tag, _wow_cart_exp: CFG.experiment } })
      .catch(function () { return c; });
  }

  /* ---------- watch cart changes made by the theme and other apps ---------- */

  var onExternalChange = function () {};

  function isExternalMutation(url) {
    // Mule marks its own requests with provider=mule; reacting to those would
    // have the two scripts answer each other forever.
    return ownCalls === 0 && isCartMutation(url) && !/[?&]provider=mule\b/.test(String(url));
  }

  function watchCart() {
    var nativeFetch = root.fetch;
    root.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url);
      var external = isExternalMutation(url);
      var p = nativeFetch.apply(this, arguments);
      if (external) {
        p.then(function (r) { if (r && r.ok) onExternalChange(isCartAdd(url)); }).catch(function () {});
      }
      return p;
    };
    var open = root.XMLHttpRequest.prototype.open;
    root.XMLHttpRequest.prototype.open = function (method, url) {
      if (isExternalMutation(url)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) onExternalChange(isCartAdd(url));
        });
      }
      return open.apply(this, arguments);
    };
  }

  /* ---------- arm A: tag only ---------- */

  function startArmA() {
    if (!CFG.tag) return;
    var timer = null;
    onExternalChange = function () {
      clearTimeout(timer);
      timer = setTimeout(function () { api('/cart.js').then(tagCart).catch(function () {}); }, 800);
    };
    watchCart();
    api('/cart.js').then(tagCart).catch(function () {});
  }

  /* ---------- arm B: the cart ---------- */

  var el, refs = {};

  var ICON = {
    trash: '<svg width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden="true"><path d="M1 4h12M5 4V2.5A1.5 1.5 0 016.5 1h1A1.5 1.5 0 019 2.5V4m2.5 0l-.6 9.1a1.5 1.5 0 01-1.5 1.4H4.6a1.5 1.5 0 01-1.5-1.4L2.5 4M5.5 7v4.5M8.5 7v4.5" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    minus: '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 5h8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
    plus: '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 5h8M5 1v8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>',
    gift: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 8v6h12V8M1 5h14v3H1zM8 5v9M8 5S7.2 1.5 5 1.5a1.75 1.75 0 000 3.5h3zm0 0s.8-3.500 3-3.500a1.75 1.75 0 010 3.500H8z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    truck: '<svg width="21" height="16" viewBox="0 0 21 16" fill="none" aria-hidden="true"><path d="M17.64 4.49h-2.68V.92H2.47C1.49.92.68 1.73.68 2.71v9.81h1.79a2.68 2.68 0 005.35 0h5.36a2.68 2.68 0 005.35 0h1.79V8.06l-2.68-3.57zm-.45 1.34l1.75 2.23h-3.98V5.83h2.23zM5.15 13.42a.9.9 0 110-1.79.9.9 0 010 1.79zm1.98-2.68a2.66 2.66 0 00-3.96 0h-.7V2.71h10.71v8.03H7.13zm8.72 2.68a.9.9 0 110-1.79.9.9 0 010 1.79z" fill="currentColor"/></svg>'
  };

  function img(src, width) {
    if (!src) return '';
    var u = String(src).replace(/^https?:/, '');
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Free gifts switched on (storefront/wow-gift, discountLive): the bar shows
  // free shipping plus one step per gift, laid out like Rebuy's tiered bar.
  function renderTieredProgress(gift) {
    var tiers = [{ type: 'shipping', threshold_cents: CFG.freeShippingCents, label: 'Free Shipping' }].concat(gift.tiers);
    var s = tierProgress(cart, tiers, CFG.excludeHandles);
    var prompt;
    if (s.next && s.next.type === 'shipping') {
      prompt = 'You are ' + money(s.remaining) + ' away from <b>FREE SHIPPING</b>!';
    } else if (s.next) {
      prompt = 'You are ' + money(s.remaining) + ' away from a free ' + escapeHtml(s.next.title) + '!';
    } else if (s.last.type === 'shipping') {
      prompt = '🎉 Congrats! You\'ve unlocked <b>FREE SHIPPING!</b>';
    } else {
      prompt = 'You have unlocked a free ' + escapeHtml(s.last.title) + '!';
    }
    // Rebuy's order on this theme: the steps, then the sentence, then the bar
    refs.progress.innerHTML =
      '<div class="wow-cart__steps wow-cart__steps--' + s.steps.length + '">' +
        s.steps.map(function (step) {
          return '<div class="wow-cart__step' + (step.reached ? ' is-complete' : '') + '">' +
            '<div class="wow-cart__step-icon">' + (step.type === 'shipping' ? ICON.truck : ICON.gift) + '</div>' +
            '<span class="wow-cart__step-label">' + escapeHtml(step.label) + '</span></div>';
        }).join('') +
      '</div>' +
      '<div class="wow-cart__progress-prompt">' + prompt + '</div>' +
      '<div class="wow-cart__meter" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + s.percent + '">' +
      '<div class="wow-cart__meter-fill" style="width:' + s.percent + '%"></div></div>';
  }

  function renderProgress() {
    if (!cart.item_count) { refs.progress.innerHTML = ''; return; }
    var gift = root.WowGift;
    if (gift && gift.live && gift.tiers && gift.tiers.length) { renderTieredProgress(gift); return; }
    var s = progressState(cart, CFG.freeShippingCents, CFG.excludeHandles);
    var prompt = s.reached
      ? '\uD83C\uDF89 Congrats! You\'ve unlocked <b>FREE SHIPPING!</b>'
      : 'You are ' + money(s.remaining) + ' away from <b>FREE SHIPPING</b>!';
    refs.progress.innerHTML =
      '<div class="wow-cart__progress-prompt">' + prompt + '</div>' +
      '<div class="wow-cart__meter" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + s.percent + '">' +
      '<div class="wow-cart__meter-fill" style="width:' + s.percent + '%"></div></div>';
  }

  function renderItem(item, index) {
    var line = index + 1;
    var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    var props = Object.keys(item.properties || {}).filter(function (k) {
      return k.charAt(0) !== '_' && item.properties[k] !== '';
    }).map(function (k) {
      return '<div class="wow-cart__item-prop">' + escapeHtml(k) + ': ' + escapeHtml(item.properties[k]) + '</div>';
    }).join('');
    var price = item.original_line_price > item.final_line_price
      ? '<s>' + money(item.original_line_price) + '</s> <span class="wow-cart__sale">' + money(item.final_line_price) + '</span>'
      : money(item.final_line_price);
    return '' +
      '<div class="wow-cart__item" data-line="' + line + '" data-key="' + escapeHtml(item.key) + '">' +
        '<a class="wow-cart__item-media" href="' + escapeHtml(item.url) + '">' +
          (item.image ? '<img src="' + escapeHtml(img(item.image, 180)) + '" alt="' + escapeHtml(item.product_title) + '">' : '') +
        '</a>' +
        '<div class="wow-cart__item-info">' +
          '<div class="wow-cart__item-head">' +
            '<a class="wow-cart__item-title" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a>' +
            '<button type="button" class="wow-cart__item-remove" data-wow-remove aria-label="Remove ' + escapeHtml(item.product_title) + '">' + ICON.trash + '</button>' +
          '</div>' +
          (variant ? '<div class="wow-cart__item-variant">' + escapeHtml(variant) + '</div>' : '') +
          props +
          '<div class="wow-cart__item-row">' +
            // a free gift is one per order: no quantity buttons, only the bin
            (isGiftLine(item) ? '<div class="wow-cart__gift-tag">Free gift</div>' :
            '<div class="wow-cart__qty">' +
              '<button type="button" data-wow-qty="-1" aria-label="Decrease quantity of ' + escapeHtml(item.product_title) + '">' + ICON.minus + '</button>' +
              '<span class="wow-cart__qty-label" aria-live="polite">' + item.quantity + '</span>' +
              '<button type="button" data-wow-qty="1" aria-label="Increase quantity of ' + escapeHtml(item.product_title) + '">' + ICON.plus + '</button>' +
            '</div>') +
            '<div class="wow-cart__item-price">' + price + '</div>' +
          '</div>' +
        '</div>' +
        '<div class="wow-cart__item-sub" data-wow-sub></div>' +
      '</div>';
  }

  function renderSubscription(node, item, product) {
    if (isGiftLine(item)) { node.innerHTML = ''; return; }
    if (!product || !product.selling_plans || !product.selling_plans.length) { node.innerHTML = ''; return; }
    var plans = product.selling_plans;
    var current = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (!current) {
      node.innerHTML = '<button type="button" class="wow-cart__upgrade" data-wow-plan="' + plans[0].id + '" ' +
        'aria-label="Switch ' + escapeHtml(item.product_title) + ' to a Subscription">' +
        escapeHtml(upgradeLabel(plans[0])) + '</button>';
      return;
    }
    var active = plans.filter(function (p) { return p.id === current.id; })[0];
    var pct = planPercent(active);
    node.innerHTML =
      '<select class="wow-cart__plan-select" data-wow-plan-select aria-label="Delivery frequency for ' + escapeHtml(item.product_title) + '">' +
        '<optgroup label="Pay Full Price"><option value="">One-time only</option></optgroup>' +
        '<optgroup label="' + (pct ? 'Subscribe and Save ' + pct + '%' : 'Subscribe') + '">' +
          plans.map(function (p) {
            return '<option value="' + p.id + '"' + (p.id === current.id ? ' selected' : '') + '>' +
              escapeHtml(intervalLabel(p.interval)) + '</option>';
          }).join('') +
        '</optgroup>' +
      '</select>';
  }

  function renderItems() {
    if (!cart.item_count) {
      refs.items.innerHTML =
        '<div class="wow-cart__empty"><h4>Your cart is empty!</h4>' +
        '<p>Add your favorite items to your cart.</p>' +
        '<p><a href="/collections/all">Shop Now</a></p></div>';
      return;
    }
    refs.items.innerHTML = cart.items.map(renderItem).join('');
    cart.items.forEach(function (item, i) {
      getProduct(item.handle).then(function (product) {
        var node = refs.items.querySelector('[data-line="' + (i + 1) + '"][data-key="' + cssEscape(item.key) + '"] [data-wow-sub]');
        if (node) renderSubscription(node, item, product);
      });
    });
  }

  function cssEscape(s) {
    return root.CSS && root.CSS.escape ? root.CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&');
  }

  function stars(rating) {
    var pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
    return '<span class="wow-cart__stars" aria-label="' + Number(rating).toFixed(1) + ' out of 5 stars">' +
      '<span class="wow-cart__stars-fill" style="width:' + pct + '%"></span></span>';
  }

  function renderUpsellCard(p) {
    var buyable = p.variants.filter(function (v) { return v.available; });
    var first = buyable[0];
    var multi = !p.has_only_default_variant && buyable.length > 1;
    var review = Number(p.rating_count) > 0
      ? '<div class="wow-cart__rec-review">' + stars(p.rating) +
        '<span class="wow-cart__rec-count">( ' + Number(p.rating_count) + ' Reviews )</span></div>'
      : '';
    var action = multi
      ? '<button type="button" class="wow-cart__rec-btn" data-wow-rec-choose>' + escapeHtml(p.option_name || 'Options') + '</button>' +
        '<div class="wow-cart__rec-choose" hidden>' +
          '<select aria-label="' + escapeHtml(p.option_name || 'Options') + '" data-wow-rec-variant>' +
            buyable.map(function (v) {
              return '<option value="' + v.id + '">' + escapeHtml(v.title) + ' - ' + money(v.price) + '</option>';
            }).join('') +
          '</select>' +
          '<button type="button" class="wow-cart__rec-btn" data-wow-rec-add>Add</button>' +
        '</div>'
      : '<button type="button" class="wow-cart__rec-btn" data-wow-rec-add data-variant="' + first.id + '">Add</button>';
    return '' +
      '<div class="wow-cart__rec" data-handle="' + escapeHtml(p.handle) + '">' +
        '<a class="wow-cart__rec-media" href="' + escapeHtml(p.url) + '">' +
          (p.image ? '<img src="' + escapeHtml(img(first.image || p.image, 180)) + '" alt="View ' + escapeHtml(p.title) + '">' : '') +
        '</a>' +
        '<div class="wow-cart__rec-info">' +
          review +
          '<a class="wow-cart__rec-title" href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a>' +
          '<div class="wow-cart__rec-price">' + money(first.price) + '</div>' +
        '</div>' +
        '<div class="wow-cart__rec-actions">' + action + '</div>' +
      '</div>';
  }

  var recsData = null;
  function getRecs() {
    if (!recsData) {
      recsData = root.fetch(CFG.recsUrl)
        .then(function (r) { if (!r.ok) throw new Error('recs ' + r.status); return r.json(); })
        .catch(function () { return { pairs: {}, bestsellers: [] }; });
    }
    return recsData;
  }

  function shopifyRecs(seed) {
    if (!seed) return Promise.resolve([]);
    return root.fetch(CFG.routes.root.replace(/\/$/, '') + '/recommendations/products.json?product_id=' + seed.product_id + '&limit=6&intent=related')
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (j) { return (j.products || []).map(function (p) { return p.handle; }); })
      .catch(function () { return []; });
  }

  var upsellRun = 0;
  function renderUpsell() {
    var run = ++upsellRun;
    if (!cart.item_count) { refs.upsell.innerHTML = ''; return; }
    var merch = { items: cart.items.filter(function (i) { return CFG.excludeHandles.indexOf(i.handle) === -1; }) };
    Promise.all([getRecs(), shopifyRecs(merch.items[merch.items.length - 1])]).then(function (r) {
      var handles = candidateHandles(merch, r[0], r[1], CFG.fallbackHandles, CFG.upsellLimit + 5)
        .filter(function (h) { return CFG.excludeHandles.indexOf(h) === -1; });
      return Promise.all(handles.map(getProduct));
    }).then(function (products) {
      if (run !== upsellRun) return;
      var picks = pickUpsells(products, cart, { limit: CFG.upsellLimit, excludeHandles: CFG.excludeHandles });
      refs.upsell.innerHTML = picks.length
        ? '<h3 class="wow-cart__upsell-title">You may also like</h3>' + picks.map(renderUpsellCard).join('')
        : '';
    });
  }

  function renderFooter() {
    refs.footer.hidden = !cart.item_count;
    refs.subtotalLabel.textContent = 'Subtotal (' + cart.item_count + (cart.item_count === 1 ? ' item)' : ' items)');
    refs.subtotal.textContent = money(cart.total_price);
    var discounts = (cart.cart_level_discount_applications || []);
    refs.discounts.textContent = discounts.length
      ? discounts.length + (discounts.length === 1 ? ' order discount applied' : ' order discounts applied')
      : '';
  }

  function updateBubble() {
    root.fetch(CFG.routes.root.replace(/\/$/, '') + '/?section_id=cart-icon-bubble')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        var target = doc.getElementById('cart-icon-bubble');
        if (!html || !target) return;
        var tmp = doc.createElement('div');
        tmp.innerHTML = html;
        var section = tmp.querySelector('.shopify-section');
        if (section) target.innerHTML = section.innerHTML;
      }).catch(function () {});
  }

  function render() {
    renderProgress();
    renderItems();
    renderUpsell();
    renderFooter();
    updateBubble();
    // Tells the other WOW pieces (the free gifts) what the cart now holds.
    try {
      doc.dispatchEvent(new root.CustomEvent('wow-cart:render', { detail: { cart: cart } }));
    } catch (e) { /* a listener failing must never break the cart */ }
  }

  function setBusy(on) {
    busy = on;
    el.classList.toggle('is-busy', on);
  }

  function refresh(nudge) {
    return api('/cart.js').then(function (c) { return tagCart(c, nudge === true); }).then(function (c) {
      // tagCart answers with the updated cart when it had to write the tag
      cart = c && c.items ? c : cart;
      render();
      return cart;
    });
  }

  function change(payload) {
    if (busy) return Promise.resolve();
    setBusy(true);
    return api('/cart/change.js', payload)
      .then(function (c) { cart = c; render(); })
      .catch(function (e) { showError(e.message); return refresh(); })
      .then(function () { setBusy(false); });
  }

  function addUpsell(variantId, button) {
    if (busy) return;
    setBusy(true);
    var label = button.textContent;
    button.textContent = 'Adding...';
    api('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1, properties: { _wow_src: 'cart-upsell' } }] })
      .then(function () { button.textContent = 'Added!'; return refresh(); })
      .catch(function (e) { button.textContent = label; showError(e.message); })
      .then(function () { setBusy(false); });
  }

  var errorTimer = null;
  function showError(message) {
    var box = el.querySelector('.wow-cart__error');
    if (!box) {
      box = doc.createElement('div');
      box.className = 'wow-cart__error';
      box.setAttribute('role', 'alert');
      refs.items.parentNode.insertBefore(box, refs.items);
    }
    box.textContent = message || 'Something went wrong. Please try again.';
    clearTimeout(errorTimer);
    errorTimer = setTimeout(function () { box.remove(); }, 6000);
  }

  var lastFocus = null;
  function open(nudge) {
    if (!el.hidden && el.classList.contains('is-visible')) {
      refresh(nudge).catch(function () {});
      return;
    }
    lastFocus = doc.activeElement;
    el.hidden = false;
    // reading the width forces a layout, so the slide-in transition runs
    void el.offsetWidth;
    el.classList.add('is-visible');
    doc.documentElement.classList.add('wow-cart-open');
    refs.flyout.focus();
    refresh(nudge).catch(function () {});
  }

  function close() {
    el.classList.remove('is-visible');
    doc.documentElement.classList.remove('wow-cart-open');
    setTimeout(function () { if (!el.classList.contains('is-visible')) el.hidden = true; }, 300);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function trapFocus(ev) {
    if (ev.key === 'Escape') { close(); return; }
    if (ev.key !== 'Tab') return;
    var f = refs.flyout.querySelectorAll('a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])');
    var visible = Array.prototype.filter.call(f, function (n) { return n.offsetParent !== null; });
    if (!visible.length) return;
    var first = visible[0], last = visible[visible.length - 1];
    if (ev.shiftKey && doc.activeElement === first) { ev.preventDefault(); last.focus(); }
    else if (!ev.shiftKey && doc.activeElement === last) { ev.preventDefault(); first.focus(); }
  }

  function lineOf(node) {
    var row = node.closest('[data-line]');
    if (!row) return null;
    var key = row.getAttribute('data-key');
    return cart.items.filter(function (i) { return i.key === key; })[0] || null;
  }

  function bind() {
    el.addEventListener('keydown', trapFocus);
    el.addEventListener('click', function (ev) {
      var t = ev.target;
      if (t.closest('[data-wow-close]')) { close(); return; }
      var item;
      if (t.closest('[data-wow-remove]')) {
        item = lineOf(t);
        if (item) change({ id: item.key, quantity: 0 });
        return;
      }
      var qty = t.closest('[data-wow-qty]');
      if (qty) {
        item = lineOf(t);
        if (item && !isGiftLine(item)) change({ id: item.key, quantity: Math.max(0, item.quantity + Number(qty.getAttribute('data-wow-qty'))) });
        return;
      }
      var plan = t.closest('[data-wow-plan]');
      if (plan) {
        item = lineOf(t);
        if (item) {
          plan.textContent = 'Upgrading...';
          change({ id: item.key, quantity: item.quantity, selling_plan: Number(plan.getAttribute('data-wow-plan')) });
        }
        return;
      }
      var choose = t.closest('[data-wow-rec-choose]');
      if (choose) {
        choose.hidden = true;
        choose.parentNode.querySelector('.wow-cart__rec-choose').hidden = false;
        return;
      }
      var add = t.closest('[data-wow-rec-add]');
      if (add) {
        var select = add.parentNode.querySelector('[data-wow-rec-variant]');
        addUpsell(select ? select.value : add.getAttribute('data-variant'), add);
      }
    });
    el.addEventListener('change', function (ev) {
      var select = ev.target.closest('[data-wow-plan-select]');
      if (!select) return;
      var item = lineOf(select);
      if (item) change({ id: item.key, quantity: item.quantity, selling_plan: select.value ? Number(select.value) : null });
    });
  }

  function retireRebuyCart() {
    function kill() {
      try {
        var sc = root.Rebuy && root.Rebuy.SmartCart;
        if (sc && typeof sc.hide === 'function') sc.hide();
        if (sc && typeof sc.destroy === 'function') sc.destroy();
      } catch (e) { /* the CSS in wow-cart-head keeps it out of sight regardless */ }
    }
    kill();
    doc.addEventListener('rebuy:smartcart.ready', kill);
    doc.addEventListener('rebuy:smartcart.show', kill);
  }

  function startArmB() {
    el = doc.getElementById('wow-cart');
    if (!el) return;
    refs = {
      flyout: el.querySelector('.wow-cart__flyout'),
      progress: el.querySelector('[data-wow-progress]'),
      items: el.querySelector('[data-wow-items]'),
      upsell: el.querySelector('[data-wow-upsell]'),
      footer: el.querySelector('[data-wow-footer]'),
      subtotalLabel: el.querySelector('[data-wow-subtotal-label]'),
      subtotal: el.querySelector('[data-wow-subtotal]'),
      discounts: el.querySelector('[data-wow-discounts]')
    };
    bind();
    var timer = null;
    onExternalChange = function (added) {
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (added) open(true); else refresh(true).catch(function () {});
      }, 150);
    };
    watchCart();
    // `api` lets the other WOW pieces write to the cart without this file
    // mistaking their request for a change made by the theme or an app.
    root.WowCart = { ready: true, open: open, close: close, refresh: refresh, api: api };
    doc.documentElement.classList.add('wow-cart-ready');
    retireRebuyCart();
    refresh().catch(function () {});
  }

  function start() {
    try {
      // Rebuy's drawer does not exist on the full cart page, so neither does ours.
      if (CFG.arm === 'B' && CFG.template !== 'cart') startArmB(); else startArmA();
    } catch (e) {
      // Anything going wrong here must leave the shopper with Rebuy's cart.
      doc.documentElement.classList.remove('wow-cart-ready');
      if (root.WowCart) root.WowCart.ready = false;
      if (root.console) root.console.error('[wow-cart] start failed', e);
    }
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : this);
